import {Component, OnInit} from '@angular/core';
import {AppContext} from "../../../../app-context";
import {ChartDataPerMeasurement} from "../../../../utils/chart-data-provider";
import {lodash} from "../../../../common/utils";
import {DashboardContext} from "../../dashboard.context";
import {
    AggregatedDataPoint,
    ConnectionType,
    DataType,
    MeasurementsResult,
    MeasurementUnit,
    MeterType
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {View} from "../../../../common/view";
import {Handler} from "../../../../common/handler";
import {DataQueryFilters} from "../../../measurements-component/measurements-handler.component";
import {Entity, EntityType} from "../../../../handlers/entity";
import {filter, startWith} from 'rxjs/operators';
import {combineLatest, distinctUntilChanged, map, of, switchMap} from "rxjs";
import {effectiveMeterParentId} from "../../../refdata/meters/submeter-role.utils";

@Component({
    selector: 'app-energy-summary-widget',
    templateUrl: './energy-summary-widget.component.html',
    styleUrls: ['./energy-summary-widget.component.scss']
})
@Handler()
export class EnergySummaryWidgetComponent extends View implements OnInit {
    appContext = AppContext;
    context = DashboardContext;
    meterDashboard: boolean;

    values: SummedValues = {
        electricityConsumption: 0,
        electricityProduction: 0,
        electricityFeedIn: 0,
        gas: 0,
        heat: 0,
        cooling: 0,
        water: 0,
        chargePointConsumption: 0
    }
    private filters: DataQueryFilters;

    ngOnInit() {
        combineLatest([this.subscribeTo("isMeterDashboard").pipe(startWith(false), distinctUntilChanged()),
            this.subscribeTo("getChartDataQueryFilters").pipe(
                filter(f => !DashboardContext.hasSelectedSources() || (f.allSelectedSources && f.allSelectedSources.length > 0)))]).pipe(
                switchMap(([meterDashboard, filters]) => {
                    this.meterDashboard = meterDashboard;
                    this.filters = filters;
                    return this.subscribeTo(this.onlyMetersSelected() || meterDashboard ? "getMeasurementsData" : "getTotals", { range: filters.timeRange });
                }),
                switchMap((d: ChartDataPerMeasurement) => {
                    if (!this.onlyMetersSelected()) {
                        return of({d, entities: []});
                    }
                    const entityIds = d.byEntity.map(m => m.entityId);
                    if (!entityIds.length) {
                        return of({d, entities: []});
                    }
                    return this.sendQuery("getEntitiesById", entityIds).pipe(map(entities => ({ d, entities })));
                })).subscribe(({ d, entities }) => {
                    this.values = this.mergeTotals(d, entities);
            });
    }

    showData(type: ConnectionType): boolean {
        if (!this.meterDashboard) return true;
        return (this.filters?.allSelectedSources?.length ? this.filters.allSelectedSources : this.filters?.sources ?? []).some(s => s.connectionType === type);
    }

    private mergeTotals(data: ChartDataPerMeasurement, entities: Entity[]): SummedValues {
        if (!this.onlyMetersSelected()) {
            return (data.totals || []).map(t => this.mergeSummedValues(this.getValues(t.measurements || {}), this.getValues(t.estimatedMeasurements || {})))
                .reduce((a, b) => this.mergeSummedValues(a, b), {
                    electricityConsumption: 0,
                    electricityFeedIn: 0,
                    electricityProduction: 0,
                    gas: 0,
                    heat: 0,
                    cooling: 0,
                    water: 0,
                    chargePointConsumption: 0
                });
        } else {
            return (this.getRelevantMeasurements(data.byEntity, entities) || []).map(t => this.mergeSummedValues(this.getValues(t.measurements || {}), this.getValues(t.estimatedMeasurements || {})))
                .reduce((a, b) => this.mergeSummedValues(a, b), {
                    electricityConsumption: 0,
                    electricityFeedIn: 0,
                    electricityProduction: 0,
                    gas: 0,
                    heat: 0,
                    cooling: 0,
                    water: 0,
                    chargePointConsumption: 0
                });
        }
    }

    private getValues(measurements: { [P in DataType]?: AggregatedDataPoint[] }): SummedValues {
        const onlyMetersSelected = this.onlyMetersSelected();
        const electricityConsumption = (measurements.electricityConsumption || [])
            .concat(measurements.electricityConsumptionOffPeak || [],
                onlyMetersSelected ? measurements.electricityIntermediateConsumption || [] : []);
        const chargePointConsumption = onlyMetersSelected
            ? (measurements.chargePointConsumption || []) .concat(measurements.chargePointIntermediateConsumption)
            : (measurements.chargePointConsumption || []);
        const waterConsumption = onlyMetersSelected
            ? (measurements.waterConsumption || []).concat(measurements.waterIntermediateConsumption)
            : (measurements.waterConsumption || []);
        return {
            electricityConsumption: this.sumData(electricityConsumption),
            electricityFeedIn: this.sumData((measurements.electricityFeedIn || []).concat(measurements.electricityFeedInOffPeak)),
            electricityProduction: this.sumData((measurements.electricityGrossProduction || [])),
            gas: this.sumData(measurements.gasConsumption),
            heat: this.sumData(measurements.heatConsumption),
            cooling: this.sumData(measurements.coolingConsumption),
            water: this.sumData(waterConsumption),
            chargePointConsumption: this.sumData(chargePointConsumption)
        };
    }

    private getRelevantMeasurements(byEntity: MeasurementsResult[], entities: Entity[]): MeasurementsResult[] {
        if (this.meterDashboard) {
            return this.getRelevantMeasurementsFromEntities(byEntity, entities);
        }
        return byEntity.filter(measurement => {
            const sourceEntity = this.filters?.sources.find(source => source.source.meter.meterId === measurement.entityId);
            if (!sourceEntity) return false;
            const meter = sourceEntity.source.meter;
            if (!meter) return false;
            if (meter.info?.type === MeterType.COOLING) return true;
            const isIntermediate = meter.info?.type === MeterType.INTERMEDIATE;
            if (!isIntermediate && !meter.parentId) return true;

            const parentId = effectiveMeterParentId(meter, sourceEntity.source.connection?.meters || []);
            const parentInSources = parentId && this.filters.sources.some(s => s.source.meter?.meterId === parentId);
            const primaryMeterSelected = !meter.parentId && isIntermediate && this.filters.sources.some(s => s.source.connection?.info.code === sourceEntity.source.connection?.info.code && s.source.meter?.info.type === MeterType.PRIMARY);
            return !(parentInSources || primaryMeterSelected);
        });
    }

    private getRelevantMeasurementsFromEntities(byEntity: MeasurementsResult[], entities: Entity[]): MeasurementsResult[] {
        const entityMap = new Map(entities.map(e => [e.meter?.meterId, e]));
        return byEntity.filter(measurement => {
            const sourceEntity = entityMap.get(measurement.entityId);
            if (!sourceEntity) return false;
            const meter = sourceEntity.meter;
            if (!meter) return false;
            if (meter.info?.type === MeterType.COOLING) return true;
            const isIntermediate = meter.info?.type === MeterType.INTERMEDIATE;
            if (!isIntermediate && !meter.parentId) return true;
            const parentId = effectiveMeterParentId(meter, sourceEntity.connection?.meters || []);
            const parentInEntities = parentId && entities.some(e => e.meter?.meterId === parentId);
            const primaryMeterSelectedInEntities = !meter.parentId && isIntermediate && entities.some(
                    e => e.connection?.info.code === sourceEntity.connection?.info.code && e.meter?.info.type === MeterType.PRIMARY);
            return !(parentInEntities || primaryMeterSelectedInEntities);
        });
    }

    private mergeSummedValues = (values: SummedValues, other: SummedValues): SummedValues => {
        return {
            electricityConsumption: values.electricityConsumption + other.electricityConsumption,
            electricityFeedIn: values.electricityFeedIn + other.electricityFeedIn,
            electricityProduction: values.electricityProduction + other.electricityProduction,
            gas: values.gas + other.gas,
            heat: values.heat + other.heat,
            cooling: values.cooling + other.cooling,
            water: values.water + other.water,
            chargePointConsumption: values.chargePointConsumption + other.chargePointConsumption,
        }
    }

    sumData = (dataset: AggregatedDataPoint[]) => dataset ? lodash.sum(dataset.flatMap(d => d?.value)) || 0 : 0;

    private onlyMetersSelected = () : boolean => this.filters?.sources?.length && this.filters?.sources.every(s => s.type === EntityType.meter) || this.meterDashboard;

    electricityConsumptionUnit = () => this.context.getMeasurementUnit(DataType.electricityConsumption);
    electricityProductionUnit = () => this.context.getMeasurementUnit(DataType.electricityFeedIn);
    gasUnit = () => this.context.getMeasurementUnit(DataType.gasConsumption);
    waterUnit = () => this.context.getMeasurementUnit(DataType.waterConsumption);
    heatUnit = () => MeasurementUnit.GJ;
    coolingUnit = () => MeasurementUnit.GJ;
    showCoolingData = () => this.values.cooling !== 0 || (this.filters?.allSelectedSources || this.filters?.sources || [])
        .some(source => source.connectionType === ConnectionType.Cooling);
    chargePointConsumptionUnit = () => this.context.getMeasurementUnit(DataType.chargePointConsumption);

}

interface SummedValues {
    electricityConsumption: number;
    electricityFeedIn: number;
    electricityProduction: number;
    gas: number;
    heat: number;
    cooling: number;
    water: number;
    chargePointConsumption: number;
}
